import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Arrow,
  Circle,
  Group,
  Image as KonvaImage,
  Layer,
  Line,
  Rect,
  Stage,
  Text,
} from 'react-konva';
import type Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { uploadUrl } from '../../api/client';
import { nodeDisplayColor, nodeDisplayRadius, nodeDisplayShape, resolveNodeType } from '../../lib/nodeConfig';
import { useLayoutImage } from '../../lib/useLayoutImage';
import { fitToContent, centerViewportOnPoint, polylineCentroid, useEditorStore } from '../../store/editorStore';
import { useSimulationStore } from '../../store/simulationStore';
import { resolveGateCanvasVisual, gateNodeMetrics } from '../../lib/gateVisuals';
import { resolveGateDisplayUiState } from '../../lib/gateOverrideHelpers';
import type { ProjectDocument, ProjectNode, ProjectRoute, ProjectSettings } from '../../types/project';
import {
  type CanvasRect,
  nodesInRect,
  normalizeCanvasRect,
  resolveMarqueeModeFromDrag,
  routesInRect,
  selectionBounds,
} from '../../lib/selectionMarquee';
import NodeBodyShape from './NodeBodyShape';
import NodeContextMenu from '../nodeConfig/NodeContextMenu';
import RouteContextMenu from '../nodeConfig/RouteContextMenu';
import LayoutGridLayer from './LayoutGridLayer';
import ProductSpritesLayer from './ProductSpritesLayer';
import { effectiveGridSize } from '../../lib/grid';
import {
  buildDisplayRoutePoints,
  computeRouteDisplayOffsets,
  getRoutePortPositions,
} from '../../lib/routeGeometry';
import {
  collectGroupMemberIds,
  findGroupById,
  findGroupForNode,
  isNodeInAnyGroup,
  normalizeNodeGroups,
} from '../../lib/nodeGroups';

// ------------------------------------------------------------
// Canvas: Konva-based layout editor with pan, zoom, drag, routes
// ------------------------------------------------------------

function nodeColor(node: ProjectNode, settings?: ProjectSettings): string {
  return nodeDisplayColor(node, settings);
}

function nodeRadius(node: ProjectNode, settings?: ProjectSettings): number {
  return nodeDisplayRadius(node, settings);
}

function resolveLayoutImageUrl(
  layoutImageId?: string,
  layoutImagePath?: string,
): string | null {
  if (layoutImageId) {
    return uploadUrl(layoutImageId);
  }
  if (layoutImagePath) {
    return layoutImagePath.startsWith('/')
      ? layoutImagePath
      : `/${layoutImagePath}`;
  }
  return null;
}

function stagePointerToLayout(stage: Konva.Stage): { x: number; y: number } | null {
  const pos = stage.getRelativePointerPosition();
  if (!pos) return null;
  return { x: pos.x, y: pos.y };
}

function syncViewportFromStage(
  stage: Konva.Stage,
  viewportRef: { current: Viewport },
) {
  viewportRef.current = {
    zoom: stage.scaleX(),
    panX: stage.x(),
    panY: stage.y(),
  };
}

interface Viewport {
  zoom: number;
  panX: number;
  panY: number;
}

const MAX_ZOOM = 10;
const MIN_ZOOM = 0.15;
const ROUTE_WHITE_STROKE_DEFAULT = 6;
const ROUTE_BORDER_RATIO = 1 / 3;
const SELECTION_SHINE = '#fbbf24';
const SELECTION_SHINE_DEEP = '#f97316';
const SELECTION_GLOW = 'rgba(251, 191, 36, 0.28)';
const GROUP_SELECTION_SHINE = '#38bdf8';
const GROUP_SELECTION_SHINE_DEEP = '#0ea5e9';
const GROUP_SELECTION_GLOW = 'rgba(56, 189, 248, 0.28)';
const INGROUP_SELECTION_SHINE = '#a78bfa';
const INGROUP_SELECTION_SHINE_DEEP = '#8b5cf6';
const INGROUP_SELECTION_GLOW = 'rgba(167, 139, 250, 0.32)';

type NodeSelectionVariant = 'none' | 'ungrouped' | 'grouped' | 'ingroup';

function nodeSelectionVariant(
  nodeId: string,
  selectedNodeIdSet: Set<string>,
  document: ProjectDocument,
  focusedGroupId: string | null,
): NodeSelectionVariant {
  if (!selectedNodeIdSet.has(nodeId)) return 'none';
  if (focusedGroupId) return 'ingroup';
  return findGroupForNode(document, nodeId) ? 'grouped' : 'ungrouped';
}

function selectionPalette(variant: NodeSelectionVariant) {
  switch (variant) {
    case 'ingroup':
      return {
        shine: INGROUP_SELECTION_SHINE,
        deep: INGROUP_SELECTION_SHINE_DEEP,
        glow: INGROUP_SELECTION_GLOW,
      };
    case 'grouped':
      return {
        shine: GROUP_SELECTION_SHINE,
        deep: GROUP_SELECTION_SHINE_DEEP,
        glow: GROUP_SELECTION_GLOW,
      };
    case 'ungrouped':
      return {
        shine: SELECTION_SHINE,
        deep: SELECTION_SHINE_DEEP,
        glow: SELECTION_GLOW,
      };
    default:
      return null;
  }
}

function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

function clampNodeDisplayScale(scale: number): number {
  return Math.min(3, Math.max(0.25, scale));
}

/** Green → amber → red by normalized load 0–1 */
function congestionColor(load: number, baseColor: string): string {
  const t = Math.min(1, Math.max(0, load));
  if (t < 0.01) return baseColor;
  if (t < 0.5) {
    const u = t * 2;
    return mixHex('#34d399', '#fbbf24', u);
  }
  const u = (t - 0.5) * 2;
  return mixHex('#fbbf24', '#ef4444', u);
}

function mixHex(a: string, b: string, t: number): string {
  const parse = (h: string) => [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ];
  const [ar, ag, ab] = parse(a);
  const [br, bg, bb] = parse(b);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

function routeScreenScale(zoom: number, nodeDisplayScale: number): number {
  return nodeDisplayScale / zoom;
}

const ROUTE_BASE_POINTER_LENGTH = 10;
const ROUTE_BASE_POINTER_WIDTH = 8;
const ROUTE_LARGE_POINTER_LENGTH = 6;
const ROUTE_LARGE_POINTER_WIDTH = 5;
const ROUTE_BASE_HIT_STROKE = 14;

interface RouteVisualBases {
  strokeWidth: number;
  innerStrokeWidth: number;
  pointerLength: number;
  pointerWidth: number;
  innerPointerLength: number;
  innerPointerWidth: number;
  hitStrokeWidth: number;
}

function computeRouteVisualBases(
  route: ProjectRoute,
  isSelected: boolean,
  isLargeProject: boolean,
  simActive: boolean,
  load: number,
): RouteVisualBases {
  const whiteStrokeWidth =
    Math.max(route.width ?? ROUTE_WHITE_STROKE_DEFAULT, ROUTE_WHITE_STROKE_DEFAULT) +
    (isSelected ? 2 : 0) +
    (simActive && load > 0.35 ? 1 : 0);
  const outerStrokeWidth = whiteStrokeWidth * (1 + 2 * ROUTE_BORDER_RATIO);
  const innerRatio = whiteStrokeWidth / outerStrokeWidth;
  const pointerLength = isLargeProject
    ? ROUTE_LARGE_POINTER_LENGTH
    : ROUTE_BASE_POINTER_LENGTH;
  const pointerWidth = isLargeProject
    ? ROUTE_LARGE_POINTER_WIDTH
    : ROUTE_BASE_POINTER_WIDTH;
  return {
    strokeWidth: outerStrokeWidth,
    innerStrokeWidth: whiteStrokeWidth,
    pointerLength,
    pointerWidth,
    innerPointerLength: pointerLength * innerRatio,
    innerPointerWidth: pointerWidth * innerRatio,
    hitStrokeWidth: ROUTE_BASE_HIT_STROKE,
  };
}

function scaleRouteVisualBases(
  bases: RouteVisualBases,
  zoom: number,
  nodeDisplayScale: number,
): RouteVisualBases {
  const scale = routeScreenScale(zoom, nodeDisplayScale);
  return {
    strokeWidth: bases.strokeWidth * scale,
    innerStrokeWidth: bases.innerStrokeWidth * scale,
    pointerLength: bases.pointerLength * scale,
    pointerWidth: bases.pointerWidth * scale,
    innerPointerLength: bases.innerPointerLength * scale,
    innerPointerWidth: bases.innerPointerWidth * scale,
    hitStrokeWidth: bases.hitStrokeWidth * scale,
  };
}

interface RoutePortStyle {
  radius: number;
  strokeWidth: number;
  fill: string;
  stroke: string;
  opacity: number;
}

/** Connection cap at route endpoints — scales with route width like the track stroke. */
function computeRoutePortStyle(
  scaledVisuals: RouteVisualBases,
  isSelected: boolean,
): RoutePortStyle {
  const inner = scaledVisuals.innerStrokeWidth;
  const outer = scaledVisuals.strokeWidth;
  const border = Math.max(0.5, (outer - inner) * 0.5);
  const radius = Math.max(1.1, inner * 0.46);
  const scale = isSelected ? 1.06 : 0.92;
  return {
    radius: radius * scale,
    strokeWidth: Math.max(0.45, border),
    fill: isSelected ? '#fff7ed' : '#ffffff',
    stroke: isSelected ? SELECTION_SHINE_DEEP : '#1A1A1A',
    opacity: isSelected ? 1 : 0.9,
  };
}

function shouldShowRoutePorts(
  isSelected: boolean,
  zoom: number,
  isLargeProject: boolean,
): boolean {
  if (isSelected) return true;
  if (zoom >= 0.85) return true;
  return !isLargeProject;
}

function scaleRouteArrow(arrow: Konva.Arrow, scale: number) {
  const strokeWidth = arrow.getAttr('baseStrokeWidth') as number | undefined;
  if (strokeWidth == null) return;
  arrow.strokeWidth(strokeWidth * scale);
  arrow.pointerLength((arrow.getAttr('basePointerLength') as number) * scale);
  arrow.pointerWidth((arrow.getAttr('basePointerWidth') as number) * scale);
  const hitBase = arrow.getAttr('baseHitStrokeWidth') as number | undefined;
  if (hitBase != null && hitBase > 0) {
    arrow.hitStrokeWidth(hitBase * scale);
  }
}

function applyNodeScales(
  stage: Konva.Stage,
  zoom: number,
  nodeDisplayScale: number,
) {
  const groupScale = nodeDisplayScale / zoom;
  stage.find('.sim-node').forEach((node) => {
    node.scale({ x: groupScale, y: groupScale });
  });
}

function applyRouteVisuals(
  stage: Konva.Stage,
  zoom: number,
  nodeDisplayScale: number,
) {
  const scale = routeScreenScale(zoom, nodeDisplayScale);
  stage.find('.sim-route, .sim-route-inner').forEach((node) => {
    scaleRouteArrow(node as Konva.Arrow, scale);
  });
}

export default function SimulationCanvas() {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const viewportRef = useRef<Viewport>({ zoom: 1, panX: 0, panY: 0 });
  const nodeDisplayScaleRef = useRef(1);
  const zoomLabelRef = useRef<HTMLSpanElement>(null);
  const nodeSizeLabelRef = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState({ width: 800, height: 600 });
  const [contextMenu, setContextMenu] = useState<{
    nodeId: string;
    x: number;
    y: number;
  } | null>(null);
  const [routeContextMenu, setRouteContextMenu] = useState<{
    routeId: string;
    x: number;
    y: number;
  } | null>(null);
  const projectKeyRef = useRef<string | null>(null);
  const nodeGroupRefs = useRef<Map<string, Konva.Group>>(new Map());
  const dragOriginsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const dragAnchorRef = useRef<string | null>(null);
  const nodeMiddleClickRef = useRef<{
    nodeId: string;
    x: number;
    y: number;
  } | null>(null);
  const marqueeStartRef = useRef<{ x: number; y: number } | null>(null);
  const marqueeStartScreenRef = useRef<{ x: number; y: number } | null>(null);
  const [marquee, setMarquee] = useState<CanvasRect | null>(null);
  const [marqueeMode, setMarqueeMode] = useState<'window' | 'crossing'>('window');
  const [spacePan, setSpacePan] = useState(false);
  const [middlePan, setMiddlePan] = useState(false);

  const document = useEditorStore((s) => s.document);
  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const selectedNodeIdSet = useMemo(
    () => new Set(selectedNodeIds),
    [selectedNodeIds],
  );
  const selectedRouteIds = useEditorStore((s) => s.selectedRouteIds);
  const selectedRouteIdSet = useMemo(
    () => new Set(selectedRouteIds),
    [selectedRouteIds],
  );
  const focusedGroupId = useEditorStore((s) => s.focusedGroupId);
  const selectionFocusSeq = useEditorStore((s) => s.selectionFocusSeq);
  const routeSourceId = useEditorStore((s) => s.routeSourceId);
  const calibrationMode = useEditorStore((s) => s.calibrationMode);
  const calibrationPoints = useEditorStore((s) => s.calibrationPoints);
  const showGrid = useEditorStore((s) => s.showGrid);
  const projectId = useEditorStore((s) => s.projectId);
  const selectNode = useEditorStore((s) => s.selectNode);
  const setNodeSelection = useEditorStore((s) => s.setNodeSelection);
  const setMarqueeSelection = useEditorStore((s) => s.setMarqueeSelection);
  const openNodeEdit = useEditorStore((s) => s.openNodeEdit);
  const selectRoute = useEditorStore((s) => s.selectRoute);
  const deleteSelected = useEditorStore((s) => s.deleteSelected);
  const copySelectedNodes = useEditorStore((s) => s.copySelectedNodes);
  const cutSelectedNodes = useEditorStore((s) => s.cutSelectedNodes);
  const pasteNodes = useEditorStore((s) => s.pasteNodes);
  const nodeClipboard = useEditorStore((s) => s.nodeClipboard);
  const enterGroupFocus = useEditorStore((s) => s.enterGroupFocus);
  const exitGroupFocus = useEditorStore((s) => s.exitGroupFocus);
  const createGroupFromSelection = useEditorStore((s) => s.createGroupFromSelection);
  const addSelectedNodesToGroup = useEditorStore((s) => s.addSelectedNodesToGroup);
  const removeSelectedNodesFromGroups = useEditorStore(
    (s) => s.removeSelectedNodesFromGroups,
  );
  const selectGroupMembers = useEditorStore((s) => s.selectGroupMembers);
  const moveSelectedNodesByDelta = useEditorStore((s) => s.moveSelectedNodesByDelta);
  const permission = useEditorStore((s) => s.permission);
  const readOnly = permission !== 'edit';
  const setRouteSource = useEditorStore((s) => s.setRouteSource);
  const addRoute = useEditorStore((s) => s.addRoute);
  const addCalibrationPoint = useEditorStore((s) => s.addCalibrationPoint);
  const updateSettings = useEditorStore((s) => s.updateSettings);

  const simRunStatus = useSimulationStore((s) => s.run.status);
  const runMode = useSimulationStore((s) => s.run.runMode);
  const liveGateStates = useSimulationStore((s) => s.run.liveGateStates);
  const gateOverrides = useSimulationStore((s) => s.gateOverrides);
  const highlightedGateId = useSimulationStore((s) => s.highlightedGateId);
  const wsConnectionState = useSimulationStore((s) => s.run.wsConnectionState);
  const visualizedProductCount = useSimulationStore((s) => s.run.visualizedProductCount);
  const totalProductCount = useSimulationStore((s) => s.run.totalProductCount);
  const toggleGate = useSimulationStore((s) => s.toggleGate);
  const liveOverlay = useSimulationStore((s) => s.run.liveOverlay);
  const showProductSprites = useSimulationStore((s) => s.showProductSprites);
  const setShowProductSprites = useSimulationStore((s) => s.setShowProductSprites);
  const realtimeSim = runMode === 'realtime' && simRunStatus === 'running';
  const simActive = realtimeSim && liveOverlay != null;

  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el) return false;
      return (
        el.tagName === 'INPUT' ||
        el.tagName === 'TEXTAREA' ||
        el.isContentEditable
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat || isTypingTarget(e.target)) return;

      const { selectedNodeIds, editingNodeId } = useEditorStore.getState();
      if (editingNodeId) return;

      e.preventDefault();
      if (selectedNodeIds.length > 0) {
        useEditorStore
          .getState()
          .setNodeSelection(selectedNodeIds, { focus: true });
        return;
      }
      setSpacePan(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpacePan(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const layoutImageUrl = document
    ? resolveLayoutImageUrl(
        document.layout_image_id,
        document.layout_image_path,
      )
    : null;
  const { image: layoutImage } = useLayoutImage(layoutImageUrl);
  const layoutOpacity = document?.settings?.layout_opacity ?? 0.85;
  const gridCellSize = effectiveGridSize(document?.settings);
  const nodeDisplayScale = clampNodeDisplayScale(
    document?.settings?.node_display_scale ?? 1,
  );
  const nodeCount = document?.nodes.length ?? 0;
  const isLargeProject = nodeCount > 100;

  const nodeGroups = useMemo(
    () => (document ? normalizeNodeGroups(document) : []),
    [document],
  );

  const focusedGroup = useMemo(() => {
    if (!document || !focusedGroupId) return null;
    return findGroupById(document, focusedGroupId);
  }, [document, focusedGroupId]);

  const focusedGroupMemberIds = useMemo(() => {
    if (!document || !focusedGroup) return new Set<string>();
    return collectGroupMemberIds(focusedGroup, document.nodes);
  }, [document, focusedGroup]);

  const applyViewport = useCallback((viewport: Viewport) => {
    const stage = stageRef.current;
    if (!stage) return;
    stage.position({ x: viewport.panX, y: viewport.panY });
    stage.scale({ x: viewport.zoom, y: viewport.zoom });
    applyNodeScales(stage, viewport.zoom, nodeDisplayScaleRef.current);
    applyRouteVisuals(stage, viewport.zoom, nodeDisplayScaleRef.current);
    stage.batchDraw();
    if (zoomLabelRef.current) {
      zoomLabelRef.current.textContent = `${Math.round(viewport.zoom * 100)}%`;
    }
  }, []);

  const setViewport = useCallback(
    (next: Viewport) => {
      const viewport = {
        zoom: clampZoom(next.zoom),
        panX: next.panX,
        panY: next.panY,
      };
      viewportRef.current = viewport;
      applyViewport(viewport);
    },
    [applyViewport],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 1) return;
      const target = e.target as HTMLElement;
      if (target.tagName !== 'CANVAS') return;

      e.preventDefault();
      e.stopPropagation();

      const stage = stageRef.current;
      if (!stage) return;
      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const origin = {
        x: pointer.x,
        y: pointer.y,
        panX: viewportRef.current.panX,
        panY: viewportRef.current.panY,
      };
      setMiddlePan(true);

      const onMove = () => {
        const p = stage.getPointerPosition();
        if (!p) return;
        setViewport({
          ...viewportRef.current,
          panX: origin.panX + (p.x - origin.x),
          panY: origin.panY + (p.y - origin.y),
        });
      };

      const onUp = (upEvent: PointerEvent) => {
        if (upEvent.button !== 1) return;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        setMiddlePan(false);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };

    const onAuxClick = (e: MouseEvent) => {
      if (e.button !== 1) return;
      const target = e.target as HTMLElement;
      if (target.tagName === 'CANVAS') e.preventDefault();
    };

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('auxclick', onAuxClick);
    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('auxclick', onAuxClick);
    };
  }, [setViewport]);

  useEffect(() => {
    if (!projectId || projectKeyRef.current === projectId) return;
    projectKeyRef.current = projectId;
    const reset: Viewport = { zoom: 1, panX: 0, panY: 0 };
    viewportRef.current = reset;
    applyViewport(reset);
  }, [projectId, applyViewport]);

  useEffect(() => {
    nodeDisplayScaleRef.current = nodeDisplayScale;
    if (nodeSizeLabelRef.current) {
      nodeSizeLabelRef.current.textContent = `${Math.round(nodeDisplayScale * 100)}%`;
    }
    const stage = stageRef.current;
    if (stage) {
      applyNodeScales(stage, viewportRef.current.zoom, nodeDisplayScale);
      applyRouteVisuals(stage, viewportRef.current.zoom, nodeDisplayScale);
      stage.batchDraw();
    }
  }, [projectId, nodeDisplayScale]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleWheel = useCallback(
    (e: KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault();
      const stage = stageRef.current;
      if (!stage) return;

      const scaleBy = 1.08;
      const direction = e.evt.deltaY > 0 ? -1 : 1;

      if (e.evt.ctrlKey) {
        const oldNodeScale = nodeDisplayScaleRef.current;
        const newNodeScale = clampNodeDisplayScale(
          direction > 0 ? oldNodeScale * scaleBy : oldNodeScale / scaleBy,
        );
        nodeDisplayScaleRef.current = newNodeScale;
        applyNodeScales(stage, viewportRef.current.zoom, newNodeScale);
        applyRouteVisuals(stage, viewportRef.current.zoom, newNodeScale);
        stage.batchDraw();
        if (nodeSizeLabelRef.current) {
          nodeSizeLabelRef.current.textContent = `${Math.round(newNodeScale * 100)}%`;
        }
        if (!readOnly) {
          updateSettings({ node_display_scale: newNodeScale });
        }
        return;
      }

      const { zoom: oldScale, panX, panY } = viewportRef.current;
      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const newScale = clampZoom(
        direction > 0 ? oldScale * scaleBy : oldScale / scaleBy,
      );

      const mousePointTo = {
        x: (pointer.x - panX) / oldScale,
        y: (pointer.y - panY) / oldScale,
      };

      const next: Viewport = {
        zoom: newScale,
        panX: pointer.x - mousePointTo.x * newScale,
        panY: pointer.y - mousePointTo.y * newScale,
      };
      viewportRef.current = next;
      applyViewport(next);
    },
    [applyViewport, readOnly, updateSettings],
  );

  const handleFitToScreen = useCallback(() => {
    if (!document) return;
    const fit = fitToContent(
      document.nodes,
      document.layout_width,
      document.layout_height,
      size.width,
      size.height,
    );
    setViewport(fit);
  }, [document, size.width, size.height, setViewport]);

  useEffect(() => {
    if (selectionFocusSeq === 0 || !document || size.width <= 0 || size.height <= 0) {
      return;
    }

    const { selectedNodeIds: focusedNodeIds, selectedRouteIds: focusedRouteIds } =
      useEditorStore.getState();

    const { zoom } = viewportRef.current;
    let targetX: number | null = null;
    let targetY: number | null = null;

    if (focusedNodeIds.length > 0) {
      const selectedSet = new Set(focusedNodeIds);
      const selected = document.nodes.filter((n) => selectedSet.has(n.id));
      const center = selectionBounds(
        selected,
        nodeDisplayScaleRef.current,
        viewportRef.current.zoom,
        document.settings,
      );
      if (center) {
        targetX = center.x;
        targetY = center.y;
      }
    } else if (focusedRouteIds.length > 0) {
      const route = document.routes.find((r) => r.id === focusedRouteIds[0]);
      if (route) {
        const nodesById = new Map(document.nodes.map((n) => [n.id, n]));
        const offsets = computeRouteDisplayOffsets(document.routes);
        const points = buildDisplayRoutePoints(
          route,
          nodesById,
          offsets.get(route.id) ?? 0,
          nodeDisplayScaleRef.current,
          zoom,
          document.settings,
        );
        const centroid = polylineCentroid(points);
        if (centroid) {
          targetX = centroid.x;
          targetY = centroid.y;
        }
      }
    }

    if (targetX == null || targetY == null) return;

    setViewport(
      centerViewportOnPoint(
        targetX,
        targetY,
        size.width,
        size.height,
        zoom,
      ),
    );
  }, [selectionFocusSeq, document, size.width, size.height, setViewport]);

  const finalizeMarquee = useCallback(
    (
      rect: CanvasRect,
      pointerEvent: PointerEvent,
      mode: 'window' | 'crossing',
    ) => {
      if (!document) return;
      const routePointsById = new Map<string, number[]>();
      const nodesById = new Map(document.nodes.map((node) => [node.id, node]));
      const offsets = computeRouteDisplayOffsets(document.routes);
      const zoom = viewportRef.current.zoom;
      const nodeScale = nodeDisplayScaleRef.current;
      for (const route of document.routes) {
        const points = buildDisplayRoutePoints(
          route,
          nodesById,
          offsets.get(route.id) ?? 0,
          nodeScale,
          zoom,
          document.settings,
        );
        if (points.length >= 4) {
          routePointsById.set(route.id, points);
        }
      }

      let nodeIds = nodesInRect(
        document.nodes,
        rect,
        mode,
        nodeScale,
        zoom,
        document.settings,
      );
      let routeIds = routesInRect(routePointsById, rect, mode);
      if (focusedGroupId && focusedGroupMemberIds.size > 0) {
        nodeIds = nodeIds.filter((id) => focusedGroupMemberIds.has(id));
        routeIds = routeIds.filter((routeId) => {
          const route = document.routes.find((item) => item.id === routeId);
          if (!route) return false;
          return (
            focusedGroupMemberIds.has(route.from_node_id) &&
            focusedGroupMemberIds.has(route.to_node_id)
          );
        });
      }
      const normalized = normalizeCanvasRect(rect);
      const isClick =
        Math.abs(normalized.x2 - normalized.x1) < 4 &&
        Math.abs(normalized.y2 - normalized.y1) < 4;

      if (isClick) {
        selectNode(null);
        selectRoute(null);
        if (routeSourceId) setRouteSource(null);
      } else if (nodeIds.length > 0 || routeIds.length > 0) {
        setMarqueeSelection(nodeIds, routeIds, {
          additive: pointerEvent.shiftKey,
          subtractive: pointerEvent.ctrlKey || pointerEvent.metaKey,
        });
      } else if (
        !pointerEvent.shiftKey &&
        !pointerEvent.ctrlKey &&
        !pointerEvent.metaKey
      ) {
        selectNode(null);
        selectRoute(null);
      }

      marqueeStartRef.current = null;
      marqueeStartScreenRef.current = null;
      setMarquee(null);
    },
    [
      document,
      focusedGroupId,
      focusedGroupMemberIds,
      routeSourceId,
      selectNode,
      selectRoute,
      setMarqueeSelection,
      setRouteSource,
    ],
  );

  const handleCanvasPointerDown = useCallback(
    (e: KonvaEventObject<PointerEvent>) => {
      if (calibrationMode || simActive || spacePan) return;
      if (e.evt.button !== 0) return;

      const stage = stageRef.current;
      if (!stage) return;
      syncViewportFromStage(stage, viewportRef);
      const pt = stagePointerToLayout(stage);
      if (!pt) return;

      e.evt.preventDefault();
      const pointer = stage.getPointerPosition();
      if (!pointer) return;
      marqueeStartRef.current = pt;
      marqueeStartScreenRef.current = { x: pointer.x, y: pointer.y };
      setMarqueeMode('window');
      setMarquee({ x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y });

      const onMove = () => {
        const movePointer = stage.getPointerPosition();
        if (!movePointer || !marqueeStartRef.current || !marqueeStartScreenRef.current) {
          return;
        }
        syncViewportFromStage(stage, viewportRef);
        const current = stagePointerToLayout(stage);
        if (!current) return;
        const mode = resolveMarqueeModeFromDrag(
          marqueeStartScreenRef.current.x,
          marqueeStartScreenRef.current.y,
          movePointer.x,
          movePointer.y,
        );
        setMarqueeMode(mode);
        setMarquee({
          x1: marqueeStartRef.current.x,
          y1: marqueeStartRef.current.y,
          x2: current.x,
          y2: current.y,
        });
      };

      const onUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        const start = marqueeStartRef.current;
        const startScreen = marqueeStartScreenRef.current;
        if (!start || !startScreen) return;
        syncViewportFromStage(stage, viewportRef);
        const pointer = stage.getPointerPosition();
        const end = stagePointerToLayout(stage) ?? start;
        const mode = pointer
          ? resolveMarqueeModeFromDrag(
              startScreen.x,
              startScreen.y,
              pointer.x,
              pointer.y,
            )
          : 'window';
        finalizeMarquee(
          {
            x1: start.x,
            y1: start.y,
            x2: end.x,
            y2: end.y,
          },
          ev,
          mode,
        );
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp, { once: true });
    },
    [calibrationMode, simActive, spacePan, finalizeMarquee],
  );

  const handleNodeDoubleClick = useCallback(
    (nodeId: string) => {
      if (calibrationMode) return;
      if (!document) return;
      const group = findGroupForNode(document, nodeId);
      if (group && focusedGroupId !== group.id) {
        enterGroupFocus(group.id);
        selectNode(nodeId);
        return;
      }
      openNodeEdit(nodeId);
    },
    [
      calibrationMode,
      document,
      focusedGroupId,
      enterGroupFocus,
      selectNode,
      openNodeEdit,
    ],
  );

  const handleNodeContextMenu = useCallback(
    (nodeId: string, e: KonvaEventObject<PointerEvent>) => {
      if (calibrationMode) return;
      e.evt.preventDefault();
      e.evt.stopPropagation();
      const selected = useEditorStore.getState().selectedNodeIds;
      if (!selected.includes(nodeId)) {
        if (selected.length > 0) {
          setNodeSelection([...selected, nodeId]);
        } else {
          selectNode(nodeId);
        }
      }
      setContextMenu({ nodeId, x: e.evt.clientX, y: e.evt.clientY });
    },
    [calibrationMode, selectNode, setNodeSelection],
  );

  const handleNodePointerDown = useCallback(
    (nodeId: string, e: KonvaEventObject<PointerEvent>) => {
      if (calibrationMode || e.evt.button !== 1) return;
      const selected = useEditorStore.getState().selectedNodeIds;
      if (!selected.includes(nodeId)) return;
      e.evt.preventDefault();
      e.evt.stopPropagation();
      nodeMiddleClickRef.current = {
        nodeId,
        x: e.evt.clientX,
        y: e.evt.clientY,
      };
    },
    [calibrationMode],
  );

  const handleNodePointerUp = useCallback(
    (nodeId: string, e: KonvaEventObject<PointerEvent>) => {
      if (calibrationMode || e.evt.button !== 1) return;
      const pending = nodeMiddleClickRef.current;
      if (!pending || pending.nodeId !== nodeId) return;
      nodeMiddleClickRef.current = null;
      const dx = e.evt.clientX - pending.x;
      const dy = e.evt.clientY - pending.y;
      if (dx * dx + dy * dy > 36) return;
      setNodeSelection([nodeId], { focus: true });
    },
    [calibrationMode, setNodeSelection],
  );

  const handleNodeClick = useCallback(
    (nodeId: string, e: KonvaEventObject<MouseEvent | Event>) => {
      if (calibrationMode) return;
      if (routeSourceId && !readOnly) {
        if (routeSourceId !== nodeId) {
          addRoute(routeSourceId, nodeId);
        } else {
          setRouteSource(null);
        }
        return;
      }
      const native = e.evt as MouseEvent;
      const additive = native.shiftKey;
      const subtractive = native.ctrlKey || native.metaKey;

      if (focusedGroupId || !document) {
        selectNode(nodeId, { additive, subtractive });
        return;
      }

      const group = findGroupForNode(document, nodeId);
      if (group && !additive && !subtractive) {
        selectGroupMembers(group.id);
        return;
      }

      selectNode(nodeId, { additive, subtractive });
    },
    [
      calibrationMode,
      routeSourceId,
      readOnly,
      focusedGroupId,
      document,
      addRoute,
      setRouteSource,
      selectNode,
      selectGroupMembers,
    ],
  );

  const handleNodeDragStart = useCallback(
    (nodeId: string) => {
      if (readOnly || simActive) return;
      let selection = useEditorStore.getState().selectedNodeIds;
      if (!selection.includes(nodeId)) {
        selectNode(nodeId);
        selection = [nodeId];
      }
      const origins = new Map<string, { x: number; y: number }>();
      for (const id of selection) {
        const node = document?.nodes.find((n) => n.id === id);
        if (node) origins.set(id, { x: node.x, y: node.y });
      }
      dragOriginsRef.current = origins;
      dragAnchorRef.current = nodeId;
    },
    [document?.nodes, readOnly, simActive, selectNode],
  );

  const handleNodeDragMove = useCallback(
    (nodeId: string, e: KonvaEventObject<DragEvent>) => {
      const anchor = dragAnchorRef.current;
      if (!anchor || anchor !== nodeId) return;
      const origin = dragOriginsRef.current.get(anchor);
      if (!origin) return;
      const dx = e.target.x() - origin.x;
      const dy = e.target.y() - origin.y;
      for (const [id, pos] of dragOriginsRef.current) {
        if (id === anchor) continue;
        const group = nodeGroupRefs.current.get(id);
        group?.position({ x: pos.x + dx, y: pos.y + dy });
      }
    },
    [],
  );

  const handleNodeDragEnd = useCallback(
    (nodeId: string, e: KonvaEventObject<DragEvent>) => {
      const anchor = dragAnchorRef.current;
      if (!anchor || anchor !== nodeId) return;
      const origin = dragOriginsRef.current.get(anchor);
      if (!origin) return;
      const dx = e.target.x() - origin.x;
      const dy = e.target.y() - origin.y;
      if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) {
        moveSelectedNodesByDelta(dx, dy);
      } else {
        e.target.position({ x: origin.x, y: origin.y });
      }
      dragAnchorRef.current = null;
      dragOriginsRef.current.clear();
    },
    [moveSelectedNodesByDelta],
  );

  const handleCalibrationClick = useCallback(() => {
      const stage = stageRef.current;
      if (!stage) return;
      syncViewportFromStage(stage, viewportRef);
      const pt = stagePointerToLayout(stage);
      if (pt) addCalibrationPoint(pt.x, pt.y);
    },
    [addCalibrationPoint],
  );

  const handleRouteClick = useCallback(
    (routeId: string) => {
      if (calibrationMode || simActive) return;
      selectRoute(routeId);
    },
    [calibrationMode, simActive, selectRoute],
  );

  const handleRouteContextMenu = useCallback(
    (routeId: string, e: KonvaEventObject<PointerEvent>) => {
      if (calibrationMode || simActive) return;
      e.evt.preventDefault();
      selectRoute(routeId);
      setRouteContextMenu({ routeId, x: e.evt.clientX, y: e.evt.clientY });
    },
    [calibrationMode, simActive, selectRoute],
  );

  const nodesById = useMemo(
    () => new Map((document?.nodes ?? []).map((n) => [n.id, n])),
    [document?.nodes],
  );

  const routeDisplayOffsets = useMemo(
    () => computeRouteDisplayOffsets(document?.routes ?? []),
    [document?.routes],
  );

  const maxRouteOccupancy = useMemo(() => {
    if (!liveOverlay) return 1;
    const vals = Object.values(liveOverlay.routeOccupancy);
    return Math.max(1, ...vals, 0);
  }, [liveOverlay]);

  if (!document) {
    return (
      <div className="canvas-container flex flex-1 items-center justify-center text-text-muted">
        No document loaded
      </div>
    );
  }

  const [calP1, calP2] = calibrationPoints;
  const calibrationLinePoints =
    calP1 && calP2 ? [calP1.x, calP1.y, calP2.x, calP2.y] : null;

  const calibrationBanner = !calP1
    ? 'Click first calibration point on the layout'
    : !calP2
      ? 'Click second calibration point on the layout'
      : 'Enter the distance between points in the sidebar';

  const marqueeBox = marquee ? normalizeCanvasRect(marquee) : null;
  const marqueeWindow = marqueeMode === 'window';
  const marqueeStrokeWidth = Math.max(0.6, 1 / viewportRef.current.zoom);
  const marqueeDash = marqueeWindow
    ? undefined
    : [3 / viewportRef.current.zoom, 4 / viewportRef.current.zoom];

  return (
    <div
      ref={containerRef}
      className={`canvas-container absolute inset-0 ${
        calibrationMode ? 'calibration-mode' : ''
      } ${middlePan ? 'cursor-grabbing' : ''}`}
    >
      <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-lg border border-border bg-surface-raised/95 p-1 text-xs shadow-card backdrop-blur-sm">
        <button
          type="button"
          onClick={() => {
            const v = viewportRef.current;
            setViewport({ zoom: v.zoom / 1.2, panX: v.panX, panY: v.panY });
          }}
          className="btn-icon h-7 w-7 text-xs"
          title="Zoom out"
        >
          −
        </button>
        <span
          ref={zoomLabelRef}
          className="min-w-[3rem] px-1 text-center text-text-muted"
        >
          100%
        </span>
        <button
          type="button"
          onClick={() => {
            const v = viewportRef.current;
            setViewport({ zoom: v.zoom * 1.2, panX: v.panX, panY: v.panY });
          }}
          className="btn-icon h-7 w-7 text-xs"
          title="Zoom in"
        >
          +
        </button>
        <span className="mx-0.5 h-4 w-px bg-border" />
        <button
          type="button"
          onClick={handleFitToScreen}
          className="rounded-md px-2 py-1 text-text-muted transition hover:bg-surface-overlay hover:text-text"
          title="Fit to screen"
        >
          Fit
        </button>
        <button
          type="button"
          onClick={() => setViewport({ zoom: 1, panX: 0, panY: 0 })}
          className="rounded-md px-2 py-1 text-text-muted transition hover:bg-surface-overlay hover:text-text"
          title="Reset view"
        >
          100%
        </button>
        <span className="mx-0.5 h-4 w-px bg-border" />
        <span
          className="rounded-md px-2 py-1 text-text-muted"
          title="Ctrl + scroll on canvas to resize nodes"
        >
          Nodes{' '}
          <span ref={nodeSizeLabelRef} className="tabular-nums">
            100%
          </span>
        </span>
        {simActive && (
          <>
            <span className="mx-0.5 h-4 w-px bg-border" />
            <button
              type="button"
              onClick={() => setShowProductSprites(!showProductSprites)}
              className={`rounded-md px-2 py-1 transition ${
                showProductSprites
                  ? 'badge-accent-on-dark'
                  : 'text-text-muted hover:bg-surface-overlay hover:text-text'
              }`}
              title="Toggle product sprites on routes"
            >
              Products
            </button>
          </>
        )}
      </div>

      {spacePan && !calibrationMode && (
        <div className="absolute bottom-3 left-3 z-10 rounded-md border border-border bg-surface-raised/95 px-2 py-1 text-[10px] text-text-subtle backdrop-blur-sm">
          Space + drag to pan
        </div>
      )}
      {middlePan && (
        <div className="absolute bottom-3 left-3 z-10 rounded-md border border-border bg-surface-raised/95 px-2 py-1 text-[10px] text-text-subtle backdrop-blur-sm">
          Panning…
        </div>
      )}

      {selectedNodeIds.length > 1 && !calibrationMode && (
        <div className="absolute bottom-3 right-3 z-10 rounded-md border border-border bg-surface-raised/95 px-2 py-1 text-[10px] text-text-muted backdrop-blur-sm">
          {selectedNodeIds.length} nodes selected
        </div>
      )}

      {simRunStatus === 'running' && runMode === 'calculate' && !calibrationMode && (
        <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 animate-fade-in rounded-full border border-border bg-surface-raised/95 px-4 py-1.5 text-xs text-text-muted shadow-brand backdrop-blur-sm">
          Computing simulation… Results will appear when finished.
        </div>
      )}

      {simRunStatus === 'running' && runMode === 'realtime' && !simActive && !routeSourceId && !calibrationMode && (
        <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 animate-fade-in rounded-full border border-accent/40 badge-accent-on-dark px-4 py-1.5 text-xs shadow-brand backdrop-blur-sm">
          Starting live visualization…
        </div>
      )}

      {realtimeSim && simActive && !calibrationMode && (
        <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 animate-fade-in rounded-full border border-emerald-500/40 bg-emerald-950/80 px-4 py-1.5 text-xs text-emerald-100 shadow-brand backdrop-blur-sm">
          Real-time simulation · Click gates to divert flow
          {wsConnectionState === 'reconnecting' && ' · Reconnecting…'}
          {wsConnectionState === 'connecting' && ' · Connecting…'}
        </div>
      )}

      {realtimeSim &&
        simActive &&
        totalProductCount != null &&
        visualizedProductCount != null &&
        totalProductCount > visualizedProductCount && (
          <div className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-md border border-amber-500/40 bg-amber-950/85 px-3 py-1 text-[11px] text-amber-100 backdrop-blur-sm">
            {visualizedProductCount.toLocaleString()} of{' '}
            {totalProductCount.toLocaleString()} active products visualized.
            Colors and route density remain proportional.
          </div>
        )}

      {focusedGroup && !calibrationMode && (
        <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 animate-fade-in rounded-full border border-sky-400/40 bg-sky-950/80 px-4 py-1.5 text-xs text-sky-100 shadow-brand backdrop-blur-sm">
          Inside group: {focusedGroup.name} · Double-click a node to edit · Esc to exit
        </div>
      )}

      {routeSourceId && !calibrationMode && !focusedGroupId && (
        <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 animate-fade-in rounded-full border border-accent/40 badge-accent-on-dark px-4 py-1.5 text-xs shadow-brand backdrop-blur-sm">
          Click a destination node to connect · Esc to cancel
        </div>
      )}

      {calibrationMode && (
        <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2 animate-fade-in rounded-full border border-accent/40 badge-accent-on-dark px-4 py-1.5 text-xs shadow-brand backdrop-blur-sm">
          {calibrationBanner} · Esc to cancel
        </div>
      )}

      {contextMenu && document && (
        <NodeContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          selectedCount={selectedNodeIds.length}
          readOnly={readOnly}
          canPaste={Boolean(nodeClipboard?.length)}
          focusedGroupId={focusedGroupId}
          focusedGroupName={focusedGroup?.name ?? null}
          anchorInGroup={Boolean(findGroupForNode(document, contextMenu.nodeId))}
          anchorGroupName={
            findGroupForNode(document, contextMenu.nodeId)?.name ?? null
          }
          selectionInGroup={selectedNodeIds.some((id) =>
            isNodeInAnyGroup(document, id),
          )}
          existingGroups={nodeGroups}
          onCenter={() =>
            setNodeSelection([contextMenu.nodeId], { focus: true })
          }
          onEdit={() => openNodeEdit(contextMenu.nodeId)}
          onAddRoute={
            !readOnly && !simActive && !calibrationMode
              ? () => setRouteSource(contextMenu.nodeId)
              : undefined
          }
          onCopy={() => copySelectedNodes()}
          onCut={() => cutSelectedNodes()}
          onPaste={() => pasteNodes()}
          onDelete={() => deleteSelected()}
          onGroupSelection={
            !readOnly && selectedNodeIds.length > 0
              ? () => createGroupFromSelection()
              : undefined
          }
          onAddToGroup={
            !readOnly
              ? (groupId) => addSelectedNodesToGroup(groupId)
              : undefined
          }
          onRemoveFromGroup={
            !readOnly ? () => removeSelectedNodesFromGroups() : undefined
          }
          onEnterGroup={
            !readOnly
              ? () => {
                  const group = findGroupForNode(document, contextMenu.nodeId);
                  if (group) enterGroupFocus(group.id);
                }
              : undefined
          }
          onExitGroup={!readOnly ? () => exitGroupFocus() : undefined}
          onClose={() => setContextMenu(null)}
        />
      )}

      {routeContextMenu && !readOnly && (
        <RouteContextMenu
          x={routeContextMenu.x}
          y={routeContextMenu.y}
          onDelete={() => deleteSelected()}
          onClose={() => setRouteContextMenu(null)}
        />
      )}

      <Stage
        ref={stageRef}
        width={size.width}
        height={size.height}
        onWheel={handleWheel}
        draggable={spacePan && !calibrationMode}
        onDragEnd={(e) => {
          if (e.target === e.target.getStage()) {
            viewportRef.current = {
              ...viewportRef.current,
              panX: e.target.x(),
              panY: e.target.y(),
            };
          }
        }}
      >
        <Layer listening={false}>
          <Rect
            x={0}
            y={0}
            width={document.layout_width}
            height={document.layout_height}
            fill="#121212"
            stroke="#2E2E2E"
            strokeWidth={2}
            cornerRadius={4}
          />

          {layoutImage && (
            <KonvaImage
              image={layoutImage}
              x={0}
              y={0}
              width={document.layout_width}
              height={document.layout_height}
              opacity={layoutOpacity}
            />
          )}
        </Layer>

        <LayoutGridLayer
          width={document.layout_width}
          height={document.layout_height}
          cellSize={gridCellSize}
          visible={showGrid}
        />

        <Layer>
          {calibrationMode && (
            <Rect
              x={0}
              y={0}
              width={document.layout_width}
              height={document.layout_height}
              fill="rgba(0,0,0,0)"
              onMouseDown={handleCalibrationClick}
              onTap={handleCalibrationClick}
            />
          )}

          {!calibrationMode && !simActive && (
            <Rect
              x={0}
              y={0}
              width={document.layout_width}
              height={document.layout_height}
              fill="transparent"
              onPointerDown={handleCanvasPointerDown}
            />
          )}

          {document.routes.map((route) => {
            const zoom = viewportRef.current.zoom;
            const points = buildDisplayRoutePoints(
              route,
              nodesById,
              routeDisplayOffsets.get(route.id) ?? 0,
              nodeDisplayScale,
              zoom,
              document.settings,
            );
            if (points.length < 4) return null;
            const isSelected = selectedRouteIdSet.has(route.id);
            const showPorts = shouldShowRoutePorts(
              isSelected,
              zoom,
              isLargeProject,
            );
            const ports = showPorts
              ? getRoutePortPositions(
                  route,
                  nodesById,
                  nodeDisplayScale,
                  zoom,
                  document.settings,
                )
              : null;
            const baseStroke = '#000000';
            const occ = liveOverlay?.routeOccupancy[route.id] ?? 0;
            const load = simActive ? occ / maxRouteOccupancy : 0;
            const outerStroke = isSelected
              ? SELECTION_SHINE_DEEP
              : simActive
                ? congestionColor(load, baseStroke)
                : baseStroke;
            const routesInteractive = !calibrationMode && !simActive;
            const visualBases = computeRouteVisualBases(
              route,
              isSelected,
              isLargeProject,
              simActive,
              load,
            );
            const scaledVisuals = scaleRouteVisualBases(
              visualBases,
              viewportRef.current.zoom,
              nodeDisplayScale,
            );
            const portStyle = ports
              ? computeRoutePortStyle(scaledVisuals, isSelected)
              : null;
            return (
              <Group key={route.id}>
                <Arrow
                  name="sim-route"
                  points={points}
                  stroke={outerStroke}
                  fill={outerStroke}
                  strokeWidth={scaledVisuals.strokeWidth}
                  pointerLength={scaledVisuals.pointerLength}
                  pointerWidth={scaledVisuals.pointerWidth}
                  baseStrokeWidth={visualBases.strokeWidth}
                  basePointerLength={visualBases.pointerLength}
                  basePointerWidth={visualBases.pointerWidth}
                  baseHitStrokeWidth={
                    routesInteractive ? visualBases.hitStrokeWidth : 0
                  }
                  lineCap="round"
                  lineJoin="round"
                  hitStrokeWidth={
                    routesInteractive ? scaledVisuals.hitStrokeWidth : 0
                  }
                  shadowColor={isSelected ? SELECTION_SHINE : undefined}
                  shadowBlur={isSelected ? 12 : 0}
                  shadowOpacity={isSelected ? 0.85 : 0}
                  tension={0}
                  perfectDrawEnabled={false}
                  listening={routesInteractive}
                  onClick={() => handleRouteClick(route.id)}
                  onTap={() => handleRouteClick(route.id)}
                  onContextMenu={(e) => handleRouteContextMenu(route.id, e)}
                />
                <Arrow
                  name="sim-route-inner"
                  points={points}
                  stroke={isSelected ? '#fff7ed' : '#ffffff'}
                  fill={isSelected ? '#fff7ed' : '#ffffff'}
                  strokeWidth={scaledVisuals.innerStrokeWidth}
                  pointerLength={scaledVisuals.innerPointerLength}
                  pointerWidth={scaledVisuals.innerPointerWidth}
                  baseStrokeWidth={visualBases.innerStrokeWidth}
                  basePointerLength={visualBases.innerPointerLength}
                  basePointerWidth={visualBases.innerPointerWidth}
                  lineCap="round"
                  lineJoin="round"
                  tension={0}
                  perfectDrawEnabled={false}
                  listening={false}
                />
                {ports && portStyle && (
                  <>
                    <Circle
                      x={ports.from.x}
                      y={ports.from.y}
                      radius={portStyle.radius}
                      fill={portStyle.fill}
                      stroke={portStyle.stroke}
                      strokeWidth={portStyle.strokeWidth}
                      opacity={portStyle.opacity}
                      listening={false}
                      perfectDrawEnabled={false}
                    />
                    <Circle
                      x={ports.to.x}
                      y={ports.to.y}
                      radius={portStyle.radius}
                      fill={portStyle.fill}
                      stroke={portStyle.stroke}
                      strokeWidth={portStyle.strokeWidth}
                      opacity={portStyle.opacity}
                      listening={false}
                      perfectDrawEnabled={false}
                    />
                  </>
                )}
              </Group>
            );
          })}

          {calibrationLinePoints && (
            <>
              <Line
                points={calibrationLinePoints}
                stroke="#D42732"
                strokeWidth={2}
                dash={[8, 4]}
                listening={false}
              />
              {[calP1, calP2].map(
                (pt, i) =>
                  pt && (
                    <Circle
                      key={i}
                      x={pt.x}
                      y={pt.y}
                      radius={6}
                      fill="#AF151E"
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      listening={false}
                    />
                  ),
              )}
            </>
          )}

          {document.nodes.map((node) => {
            const settings = document.settings;
            const r = nodeRadius(node, settings);
            const nodeShape = nodeDisplayShape(node, settings);
            const isSelected = selectedNodeIdSet.has(node.id);
            const selectionVariant = nodeSelectionVariant(
              node.id,
              selectedNodeIdSet,
              document,
              focusedGroupId,
            );
            const selColors = selectionPalette(selectionVariant);
            const isDimmed =
              focusedGroupId != null && !focusedGroupMemberIds.has(node.id);
            const isRouteSource = node.id === routeSourceId;
            const color = nodeColor(node, settings);
            const showLabel = !isLargeProject || isSelected;
            const showShadow = !isLargeProject;
            const nodeType = resolveNodeType(node);
            const gateCanInteract = realtimeSim && nodeType === 'gate';
            const gateState =
              gateCanInteract || (simActive && nodeType === 'gate')
                ? ((liveGateStates[node.id] ??
                    (node.initial_state as string) ??
                    'open') as 'open' | 'closed')
                : null;
            const gateUiState = gateState
              ? resolveGateDisplayUiState(gateState, gateOverrides[node.id])
              : undefined;
            const gateVisual = gateState
              ? resolveGateCanvasVisual(gateState, gateUiState)
              : null;
            const gateMetrics = gateState ? gateNodeMetrics(r) : null;
            const isGateHighlighted =
              highlightedGateId === node.id ||
              (nodeType === 'gate' && selectedNodeIds.includes(node.id));
            const queueLen =
              simActive && nodeType === 'processor'
                ? (liveOverlay?.processorQueues[node.id] ?? 0)
                : 0;
            const isFlashing =
              simActive &&
              ((nodeType === 'processor' &&
                liveOverlay?.processorFlash[node.id] != null) ||
                (nodeType === 'output' &&
                  liveOverlay?.outputFlash[node.id] != null));
            return (
              <Group
                key={node.id}
                name="sim-node"
                x={node.x}
                y={node.y}
                opacity={isDimmed ? 0.35 : 1}
                ref={(group) => {
                  if (group) {
                    nodeGroupRefs.current.set(node.id, group);
                  } else {
                    nodeGroupRefs.current.delete(node.id);
                  }
                  const s =
                    nodeDisplayScaleRef.current / viewportRef.current.zoom;
                  group?.scale({ x: s, y: s });
                }}
                draggable={!calibrationMode && !readOnly && !simActive}
                listening={!calibrationMode && (!simActive || gateCanInteract)}
                onDragStart={() => handleNodeDragStart(node.id)}
                onDragMove={(e) => handleNodeDragMove(node.id, e)}
                onDragEnd={(e) => handleNodeDragEnd(node.id, e)}
                onClick={(e) => {
                  if (gateCanInteract) {
                    e.cancelBubble = true;
                    const state = (liveGateStates[node.id] ??
                      (node.initial_state as string) ??
                      'open') as 'open' | 'closed';
                    toggleGate(node.id, state === 'open' ? 'closed' : 'open');
                    return;
                  }
                  handleNodeClick(node.id, e);
                }}
                onTap={(e) => {
                  if (gateCanInteract) {
                    e.cancelBubble = true;
                    const state = (liveGateStates[node.id] ??
                      (node.initial_state as string) ??
                      'open') as 'open' | 'closed';
                    toggleGate(node.id, state === 'open' ? 'closed' : 'open');
                    return;
                  }
                  handleNodeClick(node.id, e);
                }}
                onPointerDown={(e) => handleNodePointerDown(node.id, e)}
                onPointerUp={(e) => handleNodePointerUp(node.id, e)}
                onDblClick={() => handleNodeDoubleClick(node.id)}
                onDblTap={() => handleNodeDoubleClick(node.id)}
                onContextMenu={(e) => handleNodeContextMenu(node.id, e)}
              >
                {isSelected && selColors && (
                  <Circle
                    radius={r + 12}
                    fill={selColors.glow}
                    shadowColor={selColors.shine}
                    shadowBlur={18}
                    shadowOpacity={1}
                    perfectDrawEnabled={false}
                  />
                )}
                {(isSelected || isRouteSource || isFlashing) && (
                  <Circle
                    radius={r + 8}
                    stroke={
                      isFlashing
                        ? SELECTION_SHINE
                        : isRouteSource
                          ? '#D42732'
                          : isSelected && selColors
                            ? selColors.shine
                            : SELECTION_SHINE
                    }
                    strokeWidth={isSelected || isFlashing ? 3 : 2}
                    dash={isRouteSource ? [6, 4] : isFlashing ? undefined : [5, 3]}
                    opacity={isFlashing || isSelected ? 1 : 0.9}
                    shadowColor={isSelected && selColors ? selColors.shine : undefined}
                    shadowBlur={isSelected ? 10 : 0}
                    shadowOpacity={isSelected ? 0.9 : 0}
                    perfectDrawEnabled={false}
                  />
                )}
                {gateState && gateVisual && gateMetrics && (
                  <>
                    {isGateHighlighted && (
                      <Circle
                        radius={gateMetrics.highlightRadius}
                        stroke="#38bdf8"
                        strokeWidth={gateMetrics.highlightStrokeWidth}
                        dash={[gateMetrics.highlightStrokeWidth * 2, gateMetrics.highlightStrokeWidth * 1.5]}
                        shadowColor="#38bdf8"
                        shadowBlur={gateMetrics.highlightShadowBlur}
                        shadowOpacity={0.7}
                        perfectDrawEnabled={false}
                        listening={false}
                      />
                    )}
                    <Circle
                      radius={gateMetrics.haloRadius}
                      fill={gateVisual.haloFill}
                      stroke={gateVisual.ringStroke}
                      strokeWidth={gateMetrics.haloStrokeWidth}
                      dash={
                        gateUiState === 'OPENING' || gateUiState === 'CLOSING'
                          ? [
                              gateMetrics.haloStrokeWidth * 2.5,
                              gateMetrics.haloStrokeWidth * 2,
                            ]
                          : undefined
                      }
                      perfectDrawEnabled={false}
                      listening={false}
                    />
                    <Group y={gateMetrics.badgeOffsetY} listening={false}>
                      <Rect
                        x={-gateMetrics.badgeWidth / 2}
                        y={-gateMetrics.badgeHeight / 2}
                        width={gateMetrics.badgeWidth}
                        height={gateMetrics.badgeHeight}
                        cornerRadius={gateMetrics.badgeCornerRadius}
                        fill={gateVisual.badgeFill}
                        stroke={gateVisual.ringStroke}
                        strokeWidth={gateMetrics.haloStrokeWidth * 0.75}
                      />
                      <Text
                        text={gateVisual.badgeLabel}
                        fontSize={gateMetrics.badgeFontSize}
                        fontStyle="bold"
                        fontFamily="DM Sans, sans-serif"
                        fill={gateVisual.badgeText}
                        width={gateMetrics.badgeWidth}
                        align="center"
                        y={-gateMetrics.badgeFontSize * 0.42}
                      />
                    </Group>
                  </>
                )}
                <NodeBodyShape
                  shape={nodeShape}
                  radius={r}
                  fill={gateVisual?.nodeFill ?? color}
                  stroke={
                    gateVisual?.nodeStroke ??
                    (isSelected && selColors ? selColors.shine : '#1A1A1A')
                  }
                  strokeWidth={
                    gateMetrics?.nodeStrokeWidth ?? (isSelected ? 3 : 1.5)
                  }
                  shadowColor={
                    gateVisual
                      ? gateVisual.ringStroke
                      : isSelected && selColors
                        ? selColors.shine
                        : 'black'
                  }
                  shadowBlur={
                    gateMetrics?.nodeShadowBlur ??
                    (isSelected ? 8 : showShadow ? 6 : 0)
                  }
                  shadowOpacity={
                    gateVisual ? 0.45 : isSelected ? 0.75 : showShadow ? 0.35 : 0
                  }
                  shadowOffsetY={showShadow ? 2 : 0}
                />
                {showLabel && node.display_label !== false && (
                  <Text
                    text={node.name}
                    fontSize={11}
                    fontFamily="DM Sans, sans-serif"
                    fill="#F5F5F4"
                    y={r + 6}
                    width={140}
                    align="center"
                    offsetX={70}
                    listening={false}
                  />
                )}
                {queueLen > 0 && (
                  <Group listening={false}>
                    <Circle
                      x={r - 4}
                      y={-r + 4}
                      radius={9}
                      fill="#D42732"
                      stroke="#1A1A1A"
                      strokeWidth={1}
                    />
                    <Text
                      x={r - 4}
                      y={-r + 4}
                      text={queueLen > 99 ? '99+' : String(queueLen)}
                      fontSize={8}
                      fontFamily="DM Sans, sans-serif"
                      fill="#FFFFFF"
                      align="center"
                      verticalAlign="middle"
                      offsetX={queueLen > 9 ? 7 : 3}
                      offsetY={3}
                    />
                  </Group>
                )}
              </Group>
            );
          })}

          {marqueeBox && (
            <Rect
              x={marqueeBox.x1}
              y={marqueeBox.y1}
              width={marqueeBox.x2 - marqueeBox.x1}
              height={marqueeBox.y2 - marqueeBox.y1}
              fill="rgba(251, 191, 36, 0.05)"
              stroke="rgba(251, 191, 36, 0.82)"
              strokeWidth={marqueeStrokeWidth}
              dash={marqueeDash}
              perfectDrawEnabled={false}
              listening={false}
            />
          )}
        </Layer>

        {simActive && (
          <Layer listening={false}>
            <ProductSpritesLayer
              routes={document.routes}
              nodesById={nodesById}
              routeDisplayOffsets={routeDisplayOffsets}
              nodeDisplayScale={nodeDisplayScale}
              zoom={viewportRef.current.zoom}
              pixelsPerMeter={document.settings?.pixels_per_meter ?? 0}
              visible={showProductSprites}
            />
          </Layer>
        )}
      </Stage>
    </div>
  );
}
